import type { ReactNode } from 'react';
import { useAuthorization } from './useAuthorization';
import { ForbiddenPage } from './ForbiddenPage';

interface RequireRoleProps {
  /** User must have at least one of these system roles */
  roles?: string[];
  /** User must have this permission (or be PLATFORM_ADMIN) */
  permission?: string;
  /** User must have at least one of these permissions */
  anyPermission?: string[];
  /** Custom forbidden content — if not set, shows ForbiddenPage */
  fallback?: ReactNode;
  /** If true, renders null instead of ForbiddenPage when unauthorized */
  silent?: boolean;
  /** Show inline 403 (no full-page centering) */
  inline?: boolean;
  children: ReactNode;
}

/**
 * Renders children only when the current user has the required role/permission.
 * Otherwise shows a 403 message or the custom fallback.
 */
export function RequireRole({
  roles,
  permission,
  anyPermission,
  fallback,
  silent = false,
  inline = false,
  children,
}: RequireRoleProps) {
  const { isPlatformAdmin, hasRole, can, canAny, isLoading } = useAuthorization();

  if (isLoading) return null;

  const authorized =
    isPlatformAdmin ||
    (roles ? roles.some((r) => hasRole(r as any)) : false) ||
    (permission ? can(permission) : false) ||
    (anyPermission ? canAny(...anyPermission) : false);

  // If no constraint given, treat as always authorized
  const hasConstraint = !!(roles || permission || anyPermission);
  if (!hasConstraint || authorized) {
    return <>{children}</>;
  }

  if (silent) return null;
  if (fallback !== undefined) return <>{fallback}</>;
  return <ForbiddenPage inline={inline} />;
}

/**
 * Convenience wrapper: only renders children for PLATFORM_ADMIN.
 */
export function RequirePlatformAdmin({ children, silent = false }: { children: ReactNode; silent?: boolean }) {
  return (
    <RequireRole roles={['PLATFORM_ADMIN']} silent={silent}>
      {children}
    </RequireRole>
  );
}

/**
 * Convenience wrapper: only renders children for PLATFORM_ADMIN or module admins.
 */
export function RequireModuleAdmin({ module, children, silent = false, inline = false }: {
  module: string;
  children: ReactNode;
  silent?: boolean;
  inline?: boolean;
}) {
  return (
    <RequireRole permission={`${module}.admin`} silent={silent} inline={inline}>
      {children}
    </RequireRole>
  );
}
